import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getProfile, updateProfile, uploadProfilePicture } from "@/api/profile";
import {
  getNarrative,
  updateNarrative,
  ingestNarrative,
  patchNarrative,
  type NarrativePatch,
} from "@/api/narrative";
import { listApplications, updateStatus, deleteApplication } from "@/api/applications";
import type { Profile } from "@sales-ops-amplify/shared";
import type { ProfilePatch, ApplicationQuery } from "@/types";

// ---------------------------------------------------------------------------
// Profile hooks
// ---------------------------------------------------------------------------

const PROFILE_KEY = ["profile"] as const;

export function useProfile() {
  return useQuery<Profile>({
    queryKey: PROFILE_KEY,
    queryFn: getProfile,
    staleTime: 60_000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfilePatch) => updateProfile(patch),
    onSuccess: (profile) => qc.setQueryData(PROFILE_KEY, profile),
  });
}

export function useUploadProfilePicture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadProfilePicture(file),
    onSuccess: (profile) => qc.setQueryData(PROFILE_KEY, profile),
  });
}

// ---------------------------------------------------------------------------
// Narrative hooks
// ---------------------------------------------------------------------------

const NARRATIVE_KEY = ["narrative"] as const;

export function useNarrative() {
  return useQuery({
    queryKey: NARRATIVE_KEY,
    queryFn: getNarrative,
    staleTime: 60_000,
  });
}

export function useUpdateNarrative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contentOrPatch: string | NarrativePatch) => updateNarrative(contentOrPatch),
    onSuccess: () => qc.invalidateQueries({ queryKey: NARRATIVE_KEY }),
  });
}

export function usePatchNarrative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: NarrativePatch) => patchNarrative(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: NARRATIVE_KEY }),
  });
}

export function useIngestNarrative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => ingestNarrative(text),
    onSuccess: () => qc.invalidateQueries({ queryKey: NARRATIVE_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Application hooks
// ---------------------------------------------------------------------------

const APPS_KEY = ["applications"] as const;

export function useApplications(params?: ApplicationQuery) {
  return useQuery({
    // Include serialised params in query key so refetch picks up changes
    queryKey: [...APPS_KEY, params],
    queryFn: () => listApplications(params),
    staleTime: 30_000,
  });
}

export function useUpdateStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      n,
      status,
      notes,
    }: {
      n: number;
      status: string;
      notes?: string;
    }) => updateStatus(n, status, notes),
    onSuccess: () => {
      // Invalidate both the listing and the pipeline (which also carries apps)
      qc.invalidateQueries({ queryKey: APPS_KEY });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });
}

export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (n: number) => deleteApplication(n),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: APPS_KEY });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });
}